/**
 * Hook + utilitários para PWA do Admin:
 * - Troca o manifest dinamicamente quando a rota é /admin (vs /cliente)
 * - Registra service worker /admin-service-worker.js (escopo /admin)
 * - Faz subscription de Web Push e envia para o backend
 *
 * Usado APENAS quando o usuário logado é super_admin. Sub-admins
 * não recebem prompt nem registram subscription.
 *
 * Nota: imports são LAZY (dentro das funções) para garantir que qualquer
 * problema em apiService não derrube o componente que importa este arquivo.
 */
import { useEffect } from 'react';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) out[i] = rawData.charCodeAt(i);
  return out;
}

/**
 * Troca o <link rel="manifest"> para o do admin enquanto a sessão é admin.
 * Chamar UMA vez no mount da AdminDashboard.
 */
export function aplicarManifestAdmin() {
  try {
    const links = document.querySelectorAll('link[rel="manifest"]');
    links.forEach((l) => l.parentNode.removeChild(l));
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = '/manifest-admin.json';
    document.head.appendChild(link);

    // Theme color verde da marca (sobrepõe o do cliente)
    const themes = document.querySelectorAll('meta[name="theme-color"]');
    themes.forEach((m) => m.setAttribute('content', '#00FF66'));
  } catch (e) {
    console.warn('Manifest admin: falha ao aplicar', e);
  }
}

/**
 * Registra o service worker do admin (escopo /admin).
 * Retorna a registration ou null.
 */
async function registrarServiceWorkerAdmin() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    // Remove SW antigo do cliente se estiver tentando reivindicar /admin
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const r of regs) {
      if (r.active && r.active.scriptURL.endsWith('/service-worker.js')) {
        if (r.scope.endsWith('/admin/') || r.scope.endsWith('/admin')) {
          await r.unregister();
        }
      }
    }
    const reg = await navigator.serviceWorker.register('/admin-service-worker.js', {
      scope: '/admin',
    });
    return reg;
  } catch (e) {
    console.warn('SW admin register falhou:', e);
    return null;
  }
}

/**
 * Pede permissão + cria subscription + envia ao backend.
 * Idempotente — se já está inscrito, faz refresh da subscription.
 */
export async function ativarPushAdmin() {
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'sem_suporte' };
  }

  // 1) Permissão
  let perm = Notification.permission;
  if (perm === 'default') {
    perm = await Notification.requestPermission();
  }
  if (perm !== 'granted') {
    return { ok: false, reason: 'permissao_negada' };
  }

  // 2) Registrar SW
  const reg = await registrarServiceWorkerAdmin();
  if (!reg) return { ok: false, reason: 'sw_falhou' };

  // 3) Pegar public key VAPID (import lazy do apiService pra não criar
  // dependência no carregamento do módulo)
  const { apiService } = await import('../services/api');
  let publicKey;
  try {
    const r = await apiService.api.get('/admin/push/public-key');
    publicKey = r.data.public_key || r.data.publicKey;
  } catch (e) {
    return { ok: false, reason: 'sem_public_key' };
  }
  if (!publicKey) return { ok: false, reason: 'public_key_vazia' };

  // 4) Criar/recuperar subscription
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  // 5) Enviar ao backend
  try {
    const { apiService } = await import('../services/api');
    await apiService.api.post('/admin/push/subscribe', sub.toJSON());
  } catch (e) {
    return { ok: false, reason: 'backend_subscribe_falhou', erro: e?.response?.data };
  }
  return { ok: true };
}

/**
 * Hook React: aplica manifest admin no mount.
 * Ativa push automaticamente SE o usuário for super_admin e ainda não estiver inscrito.
 */
export function useAdminPwa(usuario) {
  useEffect(() => {
    aplicarManifestAdmin();
  }, []);

  useEffect(() => {
    if (!usuario || usuario.tipo !== 'super_admin') return;
    // Ativa push de forma silenciosa — se permissão for 'default' fica esperando
    // ação do usuário (botão "Ativar notificações"). Não força prompt no boot.
    if (Notification?.permission === 'granted') {
      ativarPushAdmin().catch(() => {});
    }
  }, [usuario]);
}
